import styled from "@emotion/styled";
import { Flex } from "@radix-ui/themes";
import { observer } from "mobx-react-lite";
import React from "react";
import { useTranslation } from "react-i18next";

import { theme } from "@themes/themes";

import { useStores } from "@stores";

import { BN } from "@utils/BN";
import { formatPercent } from "@utils/Format";

import { Accordion, Separator } from "@src/ui-kit";

import { ValueText } from "./styled";

// ---------------------------------------------------------------------------
// AccountInfo: static Accordion shell (Provider, Trigger, Header) never
// re-renders.  Only the tiny content observer updates on portfolio ticks.
// ---------------------------------------------------------------------------
export const TradeboxAccountInfo: React.FC = React.memo(() => {
  const { t } = useTranslation();
  return (
    <Accordion
      triggerTestId="tradebox:account-info:trigger"
      contentTestId="tradebox:account-info:content"
      label={t("accountInfo.title")}
    >
      <TradeboxAccountInfoContent />
    </Accordion>
  );
});

const TradeboxAccountInfoContent: React.FC = observer(() => {
  const { t } = useTranslation();
  const { accountStore, balanceStore, marketStore } = useStores();
  const portfolio = accountStore.portfolio;

  if (!portfolio) return null;

  const accountEquity = new BN(portfolio.equity);
  const usedMargin = new BN(portfolio.equity).minus(new BN(accountStore.portfolio.available_balance));
  const availableMargin = new BN(portfolio.available_balance);
  const symbol = marketStore.quoteToken?.symbol ?? "USDC";
  const usedMarginPercentage = usedMargin.dividedBy(accountEquity).multipliedBy(100);
  const usedMarginPercentageFormat = usedMargin.eq(0)
    ? formatPercent(0, false, 2)
    : formatPercent(usedMarginPercentage, false, 2);

  const fundingBalance = marketStore.quoteToken
    ? balanceStore.getFundingVaultBalanceByAddress(marketStore.quoteToken.address)
    : null;

  return (
    <Flex direction="column" gap="8px">
      <Separator color="accent-7" size="4" />
      <Flex direction="column" gap="4px">
        <Flex align="center" justify="between">
          <AccountInfoLabel>{t("accountInfo.accountEquity")}</AccountInfoLabel>
          <ValueText data-testid="tradebox:account-info:equity:value">
            {accountEquity.toFormat(2)} <span>{symbol}</span>
          </ValueText>
        </Flex>
        {fundingBalance && (
          <Flex align="center" justify="between">
            <AccountInfoLabel>{t("accountInfo.fundingBalance")}</AccountInfoLabel>
            <ValueText data-testid="tradebox:account-info:funding-balance:value">
              {fundingBalance.amountFormatted} <span>{symbol}</span>
            </ValueText>
          </Flex>
        )}
        <Flex align="center" justify="between">
          <AccountInfoLabel>{t("accountInfo.usedMargin")}</AccountInfoLabel>
          <ValueText data-testid="tradebox:account-info:used-margin:value">
            {usedMargin.toFormat(2)} <span>{symbol}</span> ({usedMarginPercentageFormat})
          </ValueText>
        </Flex>
        <AccountInfoMarginBar
          data-testid="tradebox:account-info:used-margin:slider"
          value={usedMarginPercentage.toNumber()}
        />
        <Flex align="center" justify="between">
          <AccountInfoLabel>{t("accountInfo.availableMargin")}</AccountInfoLabel>
          <ValueText data-testid="tradebox:account-info:available-margin:value">
            {availableMargin.toFormat(2)} <span>{symbol}</span>
          </ValueText>
        </Flex>
      </Flex>
    </Flex>
  );
});

// --- Styled components ---
const AccountInfoLabel = styled.span`
  ${theme.font.body12};
  color: ${theme.color("gray-11")};
`;

const AccountInfoMarginBar = styled.div<{ value: number }>`
  position: relative;
  width: 100%;
  height: 2px;
  border-radius: 2px;
  overflow: hidden;

  background: ${theme.color("gray-2")};

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: ${({ value }) => `${Math.max(0, Math.min(100, value))}%`};
    height: 100%;
    border-radius: 2px;
    background: ${({ value }) => {
      if (value >= 90) return theme.color("red-strong");
      if (value >= 70) return theme.color("yellow-strong");
      return theme.color("green-strong");
    }};
  }
`;
